import { z } from 'zod';

import { BENZINGA, MTN } from './news';

const inventoryEntityItem = z.object({
  tag: z.string(),
});

const inventoryTimeseriesItem = z.object({
  snake: z.string(),
});

const inventoryWebLinkItem = z.object({
  urls: z.array(z.string()).nullish(),
});

const inventoryExternalDataItem = z.object({
  links: z.array(z.string()).nullish(),
});

const inventoryArtifactItem = z.object({
  artifact_id: z.string(),
});

const sourceSchema = z.union([z.literal(BENZINGA), z.literal(MTN)]);

const inventoryNewsItem = z.object({
  article_id: z.string(),
  title: z.string(),
  source: sourceSchema,
  published: z.string(),
});

const inventoryWatchlistItem = z.object({
  id: z.string(),
  name: z.string(),
});

const inventoryInsightItem = z.object({
  id: z.string(),
});

const isoDateString = z
  .string()
  .refine(val => !isNaN(new Date(val).getTime()), {
    message: 'Invalid date string',
  });

const inventoryEconomicCalendarItem = z.object({
  categories: z.array(z.string()).nullish(),
  countries: z.array(z.string()).nullish(),
  from: isoDateString,
  to: isoDateString,
  entity_tag: z.string().optional(),
});

export type InventoryEconomicCalendarItem = z.infer<
  typeof inventoryEconomicCalendarItem
>;

const financialStatementType = z.enum(['income', 'cashflow', 'balancesheet']);

const financialStatementsItem = z.object({
  tag: z.string(),
  statement: financialStatementType.optional(),
});

export type FinancialStatementInventoryType = z.infer<
  typeof financialStatementType
>;

const documentSchema = z.object({
  Authority: z.number(),
  Country: z.string(),
  Entity: z.string(),
  ID: z.string(),
  Kind: z.string(),
  Rank: z.number(),
});

export type DocumentSearchSchema = z.infer<typeof documentSchema>;

const documentSearchItem = z.object({
  question: z.string().optional(),
  citation_groups: z.array(z.string()).nullish(),
  documents: z.array(documentSchema).nullish(),
});

export type DocumentSearchItem = z.infer<typeof documentSearchItem>;
export type InventoryWatchlistItem = z.infer<typeof inventoryWatchlistItem>;

export type InventoryNewsItem = z.infer<typeof sourceSchema>;

/** Anchor type for KG inventory rows. */
export const inventoryKgNodeType = z.enum(['company', 'theme']);

export type InventoryKgNodeType = z.infer<typeof inventoryKgNodeType>;

/** Atomic relation (paired with `node_type` + `node_key`). */
export const inventoryKgRelationType = z.enum([
  'theme',
  'company',
  'product',
  'country',
  'region',
  'competitor',
]);

export type InventoryKgRelationType = z.infer<typeof inventoryKgRelationType>;

export const inventoryKgFilterItemSchema = z.object({
  node_type: inventoryKgNodeType,
  node_key: z.string(),
});

export type InventoryKgFilterItem = z.infer<typeof inventoryKgFilterItemSchema>;

export const inventoryKgItemSchema = z.object({
  node_type: inventoryKgNodeType,
  node_key: z.string(),
  relation_type: inventoryKgRelationType,
  filter: z.array(inventoryKgFilterItemSchema).optional(),
});

export type InventoryKgItem = z.infer<typeof inventoryKgItemSchema>;

export const conversationInventoryKnowledgeGraphKey =
  'knowledge-graph' as const;

const conversationInventorySchema = z.object({
  entity: z.object({ items: z.array(inventoryEntityItem) }).optional(),
  timeseries: z.object({ items: z.array(inventoryTimeseriesItem) }).optional(),
  artifact: z.object({ items: z.array(inventoryArtifactItem) }).optional(),
  insight: z.object({ items: z.array(inventoryInsightItem) }).optional(),
  news: z.object({ items: z.array(inventoryNewsItem) }).optional(),
  watchlist: z.object({ items: z.array(inventoryWatchlistItem) }).optional(),
  ['web-links']: z.object({ items: z.array(inventoryWebLinkItem) }).optional(),
  ['external-data']: z
    .object({ items: z.array(inventoryExternalDataItem) })
    .optional(),
  ['financial-statement']: z
    .object({ items: z.array(financialStatementsItem) })
    .optional(),
  ['document-search']: z
    .object({ items: z.array(documentSearchItem) })
    .optional(),
  [conversationInventoryKnowledgeGraphKey]: z
    .object({ items: z.array(inventoryKgItemSchema) })
    .optional(),
  ['economic-calendar']: z
    .object({ items: z.array(inventoryEconomicCalendarItem) })
    .optional(),
});

export const inventory = {
  conversationInventory: {
    path: (conversationId: string) =>
      `conversation-inventory/v1/conversations/${conversationId}`,
    schema: conversationInventorySchema,
  },
};
